import React, { useState, useEffect, useRef, useMemo } from "react";
import { Card, Tab, Nav, Row, Col, OverlayTrigger, Button, Offcanvas, Spinner } from "react-bootstrap";
import corner4 from "assets/img/illustrations/corner-4.png";
import Background from "components/common/background";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import FullCalendar from "@fullcalendar/react";
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import { Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAppContext } from "providers/appProvider";
import moment from "moment";
import i18n from "culture/i18n";
import timeGridPlugin from '@fullcalendar/timegrid';
import { fetchRoleAndClaims, handleLoadingAndAccess, fetchRoleMappings, getInitialPageSize } from "assets/js/common";
import DropdownFilter from "components/common/dropdownFilter";
import { faUser, faCheck, faClock, faClipboardList, faFileExcel, faFilePdf, faCalendarAlt } from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import { writeFile, utils } from 'xlsx-js-style';
import AdvanceTableFooter from "components/common/advance-table/advanceTableFooter";
import AdvanceTableProvider from "providers/advanceTableProvider";
import useAdvanceTable from "hooks/useAdvanceTable";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Modal, Form } from "react-bootstrap";
import { fetchLogoSrc, registerRupeeFont, getRupeeCellHooks, getUnicodePdfHooks, drawPdfText, toSafeFileName, toSafeSheetName, buildExportFileName } from "../../assets/js/common";
import Select, { components } from "react-select";
import { toast } from "react-toastify";
import { getDomainConfig } from "../../config/domainConfig";

const parseLeaveTimeToLocalHours = (isoString) => {
    if (!isoString) return null;
    const d = new Date(isoString);
    return d.getHours() + d.getMinutes() / 60;
};

const computeLeaveMeta = (leave) => {
    const startH = parseLeaveTimeToLocalHours(leave.StartTime ?? leave.startTime);
    const endH = parseLeaveTimeToLocalHours(leave.EndTime ?? leave.endTime);

    let leaveDurationHours = 0;

    if (startH !== null && endH !== null) {
        leaveDurationHours = endH >= startH ? endH - startH : 24 - startH + endH;
    }

    const isFullDay = leaveDurationHours > 8 || (startH === null && endH === null);
    const deductionHours = isFullDay ? 8 : leaveDurationHours;

    return { leaveDurationHours, isFullDay, deductionHours };
};

const getLeaveForDate = (leaveMap, employeeId, dateStr) => {
    const leaves = leaveMap[String(employeeId)] || [];
    return leaves.find((l) =>
        moment(dateStr).isBetween(l.startDate, l.endDate, "day", "[]")
    ) || null;
};

const HoursRegistration = () => {
    const {
        config: { isRTL }
    } = useAppContext();
    const { t } = useTranslation();
    const [hasAccess, setHasAccess] = useState(null);
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(true);
    const calendarRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [initialEvents, setInitialEvents] = useState([]);
    const [title, setTitle] = useState('');
    const [calendarApi, setCalendarApi] = useState({});
    const hasInitialRenderHappened = useRef(false);
    const [currentView, setCurrentView] = useState('resourceTimelineMonth');
    const [showHoursHeader, setShowHoursHeader] = useState(false);
    const [currentFilter, setCurrentFilter] = useState(t('calendar.monthView'));
    const previousRange = useRef({ start: null, end: null });
    const [showOffcanvas, setShowOffcanvas] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [resourceList, setResourceList] = useState([]);
    const [orderDetails, setOrderDetails] = useState([]);
    const [ordersLoading, setOrdersLoading] = useState(false);
    const getLocationId = () => localStorage.getItem("LocationId");
    const [locationId, setLocationId] = useState(getLocationId());
    const [isFromOpen, setIsFromOpen] = useState(false);
    const [isToOpen, setIsToOpen] = useState(false);
    const [employeeList, setEmployeeList] = useState([]);
    const [employeesLoading, setEmployeesLoading] = useState(true);
    const [dateRangeFrom, setDateRangeFrom] = useState(() => moment().startOf('month').toDate());
    const [dateRangeTo, setDateRangeTo] = useState(() => moment().endOf('month').toDate());
    const [isRangeActive, setIsRangeActive] = useState(false);
    const [visibleRange, setVisibleRange] = useState(null);
    const [isEmployeeViewToggle, setIsEmployeeViewToggle] = useState(true);
    const [clientList, setClientList] = useState([]);
    const [leaveMap, setLeaveMap] = useState({});
    const resourceTable = useAdvanceTable({
        data: resourceList,
        columns: [{ accessorKey: "title" }],
        pagination: true,
        perPage: getInitialPageSize("hoursResourceTable"),
    });
    const [employeePlannedMap, setEmployeePlannedMap] = useState({});
    const [clientPlannedMap, setClientPlannedMap]     = useState({});
    const [showInvoiceModal, setShowInvoiceModal] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState("");
    const [logoSrc, setLogoSrc] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [clientData, setClientData] = useState([]);
    const customerId = localStorage.getItem("customerId")
    const [invoiceFrom, setInvoiceFrom] = useState(() =>
        moment().startOf("month").toDate()
    );

    const [invoiceTo, setInvoiceTo] = useState(() =>
        moment().endOf("month").toDate()
    );
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [isDownloadLoading, setIsDownloadLoading] = useState(false);
    const domainConfig = getDomainConfig();
    const currencySymbol = domainConfig.financialLabels.currencySymbol;

    useEffect(() => {
        const getLogo = async () => {
            const src = await fetchLogoSrc(customerId);
            setLogoSrc(src);
        };
        getLogo();
    }, []);

    const CustomGroupHeading = (props) => {
        const groupId = props.data?.id;
        const shouldShowDivider =
            groupId === "clients" || groupId === "proposals"; // show hr for both

        return (
            <div style={{ margin: 0, padding: 0 }}>
                {shouldShowDivider && (
                    <hr
                        style={{
                            margin: "2px 0",
                            borderColor: "#ccc",
                        }}
                    />
                )}
                <components.GroupHeading
                    {...props}
                    innerProps={{
                        ...props.innerProps,
                        style: {
                            padding: "2px 8px",
                            margin: 0,
                            fontSize: "0.9rem",
                        },
                    }}
                />
            </div>
        );
    };

    useEffect(() => {
        const fetchCustomer = async () => {
            const customerId = localStorage.getItem("customerId");
            if (!customerId) return;

            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_URL}/business/customerRoute/${customerId}`,
                    {
                        headers: {
                            Authorization: process.env.REACT_APP_BasicAuthSecret
                        }
                    }
                );

                setCustomer(res.data);
            } catch (err) {
                console.error("Error fetching customer:", err);
            }
        };

        fetchCustomer();
    }, []);

    // Syncs DatePickers to whatever the calendar's current view range is
    const syncDatePickersToView = (api) => {
        if (!api) return;
        const view = api.view;
        setDateRangeFrom(moment(view.currentStart).toDate());
        setDateRangeTo(moment(view.currentEnd).subtract(1, 'day').toDate());
    };

    // ── Fetch all employees for the current location (calendar.js pattern) ───
    useEffect(() => {
        const fetchAllEmployees = async () => {
            setEmployeesLoading(true);
            const selectedLocationId = localStorage.getItem("LocationId");
            const customerId = localStorage.getItem("customerId");

            try {
                const usersResponse = await axios.get(
                    `${process.env.REACT_APP_API_URL}/userRoute/users/allLocations`,
                    {
                        params: { customerId },
                        headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
                    }
                );

                const rolePromises = {};
                const usersWithRoles = await Promise.all(
                    usersResponse.data.map(async (user) => {
                        if (!rolePromises[user.Role]) {
                            rolePromises[user.Role] = fetchRoleMappings(user.Role, t);
                        }
                        const roleName = (await rolePromises[user.Role]) || "Unknown";
                        return { ...user, RoleName: roleName };
                    })
                );

                // Filter: only employees belonging to the selected location
                const employeesOnly = usersWithRoles.filter((user) => {
                    const role = user.RoleName;
                    return (
                        role !== t("clientText") &&
                        !user.IsDelete &&
                        user.Status === "0" &&
                        user.locations?.some(
                            (loc) => String(loc.LocationId) === String(selectedLocationId)
                        )
                    );
                });

                setEmployeeList(employeesOnly);
            } catch (err) {
                console.error("Error fetching employees:", err);
                setEmployeeList([]);
            } finally {
                setEmployeesLoading(false);
            }
        };

        fetchAllEmployees();
    }, [locationId, i18n.language]);

    useEffect(() => {
        const fetchClients = async () => {
            const selectedLocationId = localStorage.getItem("LocationId");
            const customerId = localStorage.getItem("customerId");
            if (!customerId) return;
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/relationRoute/relations`,
                    {
                        params: { locationId: selectedLocationId, customerId },
                        headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
                    }
                );
                const allClients = response.data || [];
                const filtered = allClients.filter(
                    (c) => String(c.LocationId) === String(selectedLocationId)
                );
                setClientData(filtered);
                setClientList(
                    filtered.sort((a, b) => (a.Name || "").localeCompare(b.Name || ""))
                );
            } catch (err) {
                console.error("Error fetching clients:", err);
            }
        };
        fetchClients();
    }, [locationId]);

    const handleGenerateInvoice = () => {
        if (isEmployeeViewToggle) return;
        setShowInvoiceModal(true);
    };

    const dropdownOptions = clientList;

   const handlePreviewInvoice = async () => {
        try {
            setIsPreviewLoading(true);
            const locationId = localStorage.getItem("LocationId");
            const customerId = localStorage.getItem("customerId");
            const selectedCulture = localStorage.getItem("culture");

            const selectedClient = clientData.find(
            (c) => String(c.RelationId) === String(selectedEntity)
            );

            const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/hourRegistrationRoute/previewClientInvoice`,
            {
                customer,
                client: selectedClient,
                owner: customer,
                logo: logoSrc,
                culture: selectedCulture,
                currencySymbol,
                currencyCode: domainConfig.financialLabels.currencyCode,
                finLabels: domainConfig.financialLabels,
                serviceTaxRate: domainConfig.financialLabels.serviceTaxRate,
                vatRate: domainConfig.financialLabels.vatRate,
                serviceTaxLabel: domainConfig.financialLabels.serviceTaxLabel,
                vatLabel: domainConfig.financialLabels.vatLabel,
            },
            {
                params: {
                locationId,
                customerId,
                clientId: selectedEntity,
                startDate: moment(invoiceFrom).format("YYYY-MM-DD"),
                endDate: moment(invoiceTo).format("YYYY-MM-DD"),
                },
                headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
                responseType: "blob", 
            }
            );

            const contentType = response.headers["content-type"] || "";
            if (!contentType.includes("application/pdf")) {
            console.error("Expected PDF but got:", contentType);
            toast.error(t("somethingWentWrong"), { position: "top-right", autoClose: 5000 });
            return;
            }

            const blob = new Blob([response.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            window.open(url);
        } catch (err) {
            console.error("Preview error:", err);
            toast.error(t("somethingWentWrong"), { position: "top-right", autoClose: 5000 });
        } finally {
            setIsPreviewLoading(false);
        }
    };

    const handleGenerateInvoiceSubmit = async () => {
    try {
        setIsDownloadLoading(true);

        const locationId = localStorage.getItem("LocationId");
        const customerId = localStorage.getItem("customerId");
        const selectedCulture = localStorage.getItem("culture");

        const selectedClient = clientData.find(
        (c) => String(c.RelationId) === String(selectedEntity)
        );

        const startDate = moment(invoiceFrom).format("YYYY-MM-DD");
        const endDate = moment(invoiceTo).format("YYYY-MM-DD");

        const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/hourRegistrationRoute/generateClientInvoice`,
        {
            customer,
            client: selectedClient,
            owner: customer,
            logo: logoSrc,
            culture: selectedCulture,
            currencySymbol,
            currencyCode: domainConfig.financialLabels.currencyCode,
            finLabels: domainConfig.financialLabels,
            serviceTaxRate: domainConfig.financialLabels.serviceTaxRate,
            vatRate: domainConfig.financialLabels.vatRate,
            serviceTaxLabel: domainConfig.financialLabels.serviceTaxLabel,
            vatLabel: domainConfig.financialLabels.vatLabel,
        },
        {
            params: { locationId, customerId, clientId: selectedEntity, startDate, endDate },
            headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
            responseType: "blob",
        }
        );

        const disposition = response.headers["content-disposition"] || "";
        const filenameMatch = disposition.match(/filename="?([^";\n]+)"?/);
        const fileName = filenameMatch?.[1] || `invoice_${startDate}_${endDate}.pdf`;

        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.success(t("invoiceGeneratedSuccessfully"), {
        position: "top-right",
        autoClose: 5000,
        });
        setShowInvoiceModal(false);
        resetInvoiceForm();

    } catch (err) {
        if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        console.error("Generate invoice error:", text);
        } else {
        console.error("Generate invoice error:", err);
        }
        toast.error(t("somethingWentWrong"), {
        position: "top-right",
        autoClose: 5000,
        });
    } finally {
        setIsDownloadLoading(false);
    }
    };

    const resetInvoiceForm = () => {
        setSelectedEntity("");
        setInvoiceFrom(moment().startOf("month").toDate());
        setInvoiceTo(moment().endOf("month").toDate());
    };

    const fetchHourRegistrations = async (startDate, endDate, leaveMapOverride) => {
        if (employeesLoading || !employeeList.length) return;
        const effectiveLeaveMap = leaveMapOverride || leaveMap; 

        try {
            setLoading(true);
            const locationId = localStorage.getItem("LocationId");
            const customerId = localStorage.getItem("customerId");

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/hourRegistrationRoute/all`,
                {
                    params: {
                        locationId,
                        customerId,
                        startDate: moment(startDate).format("YYYY-MM-DD"),
                        endDate: moment(endDate).format("YYYY-MM-DD"),
                    },
                    headers: {
                        Authorization: process.env.REACT_APP_BasicAuthSecret,
                    }
                }
            );

            const data = response.data || [];

            const employeeMap = Object.fromEntries(
                employeeList.map((emp) => [String(emp.UserId), emp])
            );

            const employeeIds = employeeList.map((emp) => String(emp.UserId));

            const dataByKey = {};
            data.forEach((item) => {
                const key = `${item.EmployeeId}-${item.WorkDate}`;
                dataByKey[key] = item;
            });

            const events = [];
            let current = moment(startDate);
            const end = moment(endDate);

            while (current.isBefore(end)) {
                const workDate = current.format("YYYY-MM-DD");

                employeeIds.forEach((id) => {
                    const key = `${id}-${workDate}`;
                    const item = dataByKey[key];

                    const rawWorkedHours = item ? Number(item.TotalWorkedHours) || 0 : 0;

                    // ── Apply leave deduction ──────────────────────────────────
                    const leaveEntry = getLeaveForDate(effectiveLeaveMap, id, workDate);
                    let netWorkedHours = rawWorkedHours;
                    let leaveDeductionHours = 0;

                    if (leaveEntry) {
                        leaveDeductionHours = leaveEntry.deductionHours;
                        // Deduct leave hours but never go below 0
                        netWorkedHours = Math.max(0, rawWorkedHours - leaveDeductionHours);
                    }

                    events.push({
                        id: key,
                        resourceId: String(id),
                        start: workDate,
                        end: workDate,
                        allDay: true,
                        extendedProps: {
                            workedHours: netWorkedHours,
                            rawWorkedHours,
                            leaveDeductionHours,
                            leaveType: leaveEntry?.type || null,
                            isFullDayLeave: leaveEntry?.isFullDay || false,
                            isOnLeave: !!leaveEntry,
                            orders: item?.Orders || [],
                            employeeName: employeeMap[String(id)]?.Name,
                            avatar: employeeMap[String(id)]?.ProfilePicUrl || null,
                            employeeId: String(id),
                        },
                    });
                });

                current.add(1, "day");
            }

            const workedByEmployee = {};
            const leaveDeductedByEmployee = {};
            events.forEach((ev) => {
                const id = ev.resourceId;
                workedByEmployee[id] =
                    (workedByEmployee[id] || 0) + (ev.extendedProps.workedHours || 0);
                leaveDeductedByEmployee[id] =
                    (leaveDeductedByEmployee[id] || 0) + (ev.extendedProps.leaveDeductionHours || 0);
            });

            const resources = employeeIds.map((id) => {
                const emp = employeeMap[id];

                const perDay =
                    emp?.FixedHours && !isNaN(emp.FixedHours)
                        ? Number(emp.FixedHours)
                        : 8;

                const hourlyRate =
                    emp?.HourlyRate && !isNaN(emp.HourlyRate)
                        ? Number(emp.HourlyRate)
                        : 0;

                let totalDays = 0;
                let tmp = moment(startDate);
                while (tmp.isBefore(endDate)) {
                    totalDays++;
                    tmp.add(1, "day");
                }

                const rawExpected = perDay * totalDays;
                const totalLeaveDeducted = leaveDeductedByEmployee[id] || 0;
                const expected = Math.max(0, rawExpected - totalLeaveDeducted);

                return {
                    id,
                    title: emp?.Name || `Employee ${id}`,
                    extendedProps: {
                        avatar: emp?.ProfilePicUrl || null,
                        workedHours: workedByEmployee[id] || 0,
                        expectedHours: expected,
                        fixedHours: emp?.FixedHours || null,
                        fixedRate: hourlyRate,
                    },
                };
            });

            if (resources.length === 0) {
                const calendarApiInst = calendarRef.current?.getApi?.();
                const viewType = calendarApiInst?.view?.type;

                let noDataText = t("noDataMonth");
                if (viewType === "timeGridWeek" || viewType === "resourceTimelineWeek") {
                    noDataText = t("noDataWeek");
                }

                setResourceList([
                    {
                        id: "no-data",
                        title: noDataText,
                        eventColor: "transparent",
                    },
                ]);
                setInitialEvents([]);
            } else {
                const sortedResources = [...resources]
                    .sort((a, b) => (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' }))
                    .map((r, i) => ({ ...r, sortIndex: i }));

                setResourceList(sortedResources);
                setInitialEvents(events);
            }
        } catch (err) {
            console.error("Error fetching hour registrations:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchUnavailability = async (startDate, endDate) => {
        try {
            const customerId = localStorage.getItem("customerId");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/userUnavailabilityRoute/unavailabilityByCustomerIdWithDate`,
                {
                    params: {
                        customerId,
                        startDate: moment(startDate).format("YYYY-MM-DD"),
                        endDate: moment(endDate).format("YYYY-MM-DD"),
                    },
                    headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
                }
            );
            const data = Array.isArray(response.data) ? response.data : [];
            // Build map: employeeId -> [{ startDate, endDate, status, type }]
            const map = {};
            data.forEach((leave) => {
                const status = (leave.Status || leave.status || "").toLowerCase();
                const type = (leave.Type || leave.type || "").toLowerCase();

                // Only show leaves that are strictly approved
                if (status !== "approved") return;

                const empId = String(leave.UserId);

                // ── Compute leave duration in local time ───────────────────────
                const { leaveDurationHours, isFullDay, deductionHours } = computeLeaveMeta(leave);

                if (!map[empId]) map[empId] = [];
                map[empId].push({
                    startDate: moment(leave.StartDate || leave.startDate).format("YYYY-MM-DD"),
                    endDate: moment(leave.EndDate || leave.endDate).format("YYYY-MM-DD"),
                    type,
                    leaveDurationHours,
                    isFullDay,
                    deductionHours,
                });
            });
            setLeaveMap(map);
            return map;
        } catch (err) {
            console.error("Error fetching unavailability:", err);
            return {};
        }
    };

    const fetchPlannedHours = async (startDate, endDate) => {
        try {
            const locationId = localStorage.getItem("LocationId");
            const customerId = localStorage.getItem("customerId");

            const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/orderRoute/plannedHours`,
            {
                params: {
                locationId,
                customerId,
                startDate: moment(startDate).format("YYYY-MM-DD"),
                endDate:   moment(endDate).format("YYYY-MM-DD"),
                },
                headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
            }
            );

            const { byEmployee = [], byClient = [] } = response.data || {};

            const empMap = {};
            byEmployee.forEach(item => {
            empMap[`${item.EmployeeId}-${item.WorkDate}`] = item;
            });

            const cliMap = {};
            byClient.forEach(item => {
            cliMap[`${item.ClientId}-${item.WorkDate}`] = item;
            });

            setEmployeePlannedMap(empMap);
            setClientPlannedMap(cliMap);
        } catch (err) {
            console.error("Error fetching planned hours:", err);
        }
    };

    const fetchClientTotalHours = async (startDate, endDate) => {
        if (!clientList.length) return;
        try {
            setLoading(true);

            const locationId = localStorage.getItem("LocationId");
            const customerId = localStorage.getItem("customerId");

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/hourRegistrationRoute/getClientTotalHours`,
                {
                    params: {
                        locationId,
                        customerId,
                        startDate: moment(startDate).format("YYYY-MM-DD"),
                        endDate: moment(endDate).format("YYYY-MM-DD"),
                    },
                    headers: { Authorization: process.env.REACT_APP_BasicAuthSecret },
                }
            );

            const data = response.data || [];

            const activeClientIds = new Set(
                data.map(item => String(item.ClientId).toUpperCase())
            );

            // ── Build lookup: ClientId (uppercase) → API item ──────────────────
            const dataByKey = {};
            data.forEach((item) => {
                const key = `${String(item.ClientId).toUpperCase()}-${item.WorkDate}`;
                dataByKey[key] = item;
            });

            const events = [];
            let current = moment(startDate);
            const end = moment(endDate);

            while (current.isBefore(end)) {
                const workDate = current.format("YYYY-MM-DD");

                clientList
                    .filter(client => activeClientIds.has(String(client.RelationId).toUpperCase()))
                    .forEach((client) => {
                        const clientId = String(client.RelationId).toUpperCase();
                        const key = `${clientId}-${workDate}`;
                        const item = dataByKey[key];

                        const employees = item?.Employees
                            ? item.Employees.map((emp) => ({
                                EmployeeId: String(emp.EmployeeId),
                                Orders: (emp.Orders || []).map((order) => ({
                                    OrderId: order.OrderId,
                                    OrderDetails: (order.OrderDetails || []).map((detail) => ({
                                        OrderDetailId: detail.OrderDetailId,
                                        TotalHours: detail.TotalHours,
                                        BreakTime: detail.BreakTime,
                                        ActualStartDateTime: detail.ActualStartDateTime,
                                        ActualEndDateTime: detail.ActualEndDateTime,
                                    })),
                                })),
                            }))
                            : [];

                        events.push({
                            id: `${clientId}-${workDate}`,
                            resourceId: clientId,
                            start: workDate,
                            end: workDate,
                            allDay: true,
                            extendedProps: {
                                workedHours: item ? Number(item.TotalWorkedHours) || 0 : 0,
                                totalBreakTime: item ? Number(item.TotalBreakTime) || 0 : 0,
                                employees,
                                clientName: client.Name,
                                clientId,
                            },
                        });
                    });

                current.add(1, "day");
            }

            const workedByClient = {};
            events.forEach((ev) => {
                const id = ev.resourceId;
                workedByClient[id] = (workedByClient[id] || 0) + (ev.extendedProps.workedHours || 0);
            });

            const resources = clientList
                .filter(client => activeClientIds.has(String(client.RelationId).toUpperCase()))
                .map((client) => ({
                    id: String(client.RelationId).toUpperCase(),
                    title: client.Name,
                    extendedProps: {
                        workedHours: workedByClient[String(client.RelationId).toUpperCase()] || 0,
                        expectedHours: 0,
                        fixedRate: 0,
                        avatar: null,
                    },
                }));

            if (resources.length === 0) {
                setResourceList([{ id: "no-data", title: t("noDataMonth"), eventColor: "transparent" }]);
                setInitialEvents([]);
            } else {
                const sorted = [...resources]
                    .sort((a, b) => {
                        const aH = a.extendedProps?.workedHours || 0;
                        const bH = b.extendedProps?.workedHours || 0;
                        if (aH > 0 && bH === 0) return -1;
                        if (aH === 0 && bH > 0) return 1;
                        return (a.title || "").localeCompare(b.title || "");
                    })
                    .map((r, i) => ({ ...r, sortIndex: i }));
                setResourceList(sorted); 
                setInitialEvents(events);
            }
        } catch (err) {
            console.error("Error fetching client total hours:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchOrderDetails = async (orders) => {
        try {
            setOrdersLoading(true);
            if (!orders?.length) return [];

            const payload = {
                orders: orders.map((o) => ({
                    OrderId: o.OrderId,
                    detailIds: o.OrderDetailIds || o.OrderDetails?.map((d) => d.OrderDetailId) || [],
                })),
            };

            const res = await axios.post(
                `${process.env.REACT_APP_API_URL}/hourRegistrationRoute/detailsByIds/batch`,                
                payload,
                { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
            );

            const apiDataByOrderId = Object.fromEntries(res.data.map((d) => [d.OrderId, d]));

            return orders.map((o) => {
                const apiData = apiDataByOrderId[o.OrderId] || { OrderId: o.OrderId, OrderDetails: [] };

                if (o.OrderDetails?.length) {
                    apiData.OrderDetails = apiData.OrderDetails?.map((d) => {
                        const match = o.OrderDetails.find((ev) => ev.OrderDetailId === d.OrderDetailId);
                        return match && match.BreakTime !== undefined
                            ? { ...d, BreakTime: match.BreakTime }
                            : d;
                    }) ?? [];
                }

                return apiData;
            });
        } catch (err) {
            console.error("Error fetching order details:", err);
            return [];
        } finally {
            setOrdersLoading(false);
        }
    };

    const mergeOrderGroups = (primary = [], secondary = []) => {
        const map = {};
        [...(primary || []), ...(secondary || [])].forEach((o) => {
            if (!map[o.OrderId]) {
                map[o.OrderId] = { OrderId: o.OrderId, ClientId: o.ClientId, OrderDetails: [] };
            }
            const seen = new Set(map[o.OrderId].OrderDetails.map((d) => d.OrderDetailId));
            (o.OrderDetails || []).forEach((d) => {
                if (!seen.has(d.OrderDetailId)) {
                    map[o.OrderId].OrderDetails.push(d);
                    seen.add(d.OrderDetailId);
                }
            });
        });
        return Object.values(map);
    };

    const handleEventClick = (info) => {
        const res = info.event.getResources?.()[0];
        const worked = Number(info.event.extendedProps?.workedHours) || 0;
        const dateStr = moment(info.event.start).format("YYYY-MM-DD");

        let plannedRecord = null;
        if (isEmployeeViewToggle) {
            const employeeId = info.event.extendedProps?.employeeId;
            plannedRecord = employeePlannedMap[`${employeeId}-${dateStr}`] || null;
        } else {
            const clientId = info.event.extendedProps?.clientId;
            plannedRecord = clientPlannedMap[`${clientId}-${dateStr}`] || null;
        }
        const plannedHours = plannedRecord?.PlannedHours || 0;

        const baseSelected = {
            id: info.event.id,
            start: info.event.start,
            end: info.event.end,
            resourceId: res?.id ?? info.event.extendedProps?.clientId ?? info.event.extendedProps?.employeeId ?? null,
            employeeName: info.event.extendedProps?.clientName ?? info.event.extendedProps?.employeeName ?? res?.title ?? "",
            workedHours: worked + plannedHours,
            plannedHours,
            isPlannedOnly: worked === 0 && plannedHours > 0,
            rawWorkedHours: info.event.extendedProps?.rawWorkedHours ?? worked,
            leaveDeductionHours: info.event.extendedProps?.leaveDeductionHours ?? 0,
            leaveType: info.event.extendedProps?.leaveType ?? null,
            isFullDayLeave: info.event.extendedProps?.isFullDayLeave ?? false,
            orders: [],
            employees: info.event.extendedProps?.employees || [],
            avatar: info.event.extendedProps?.avatar || res?.extendedProps?.avatar || null,
        };

        setSelectedEvent(baseSelected);
        setShowOffcanvas(true);
        setOrdersLoading(true);

        if (!isEmployeeViewToggle) {
            const completedEmployees   = info.event.extendedProps?.employees || [];
            const plannedEmployeesList = plannedRecord?.Employees || [];

            const employeeIds = new Set([
                ...completedEmployees.map((e) => e.EmployeeId),
                ...plannedEmployeesList.map((e) => e.EmployeeId),
            ]);

            const mergedEmployees = Array.from(employeeIds).map((EmployeeId) => {
                const completed = completedEmployees.find((e) => e.EmployeeId === EmployeeId);
                const planned   = plannedEmployeesList.find((e) => e.EmployeeId === EmployeeId);
                return {
                    EmployeeId,
                    Orders: mergeOrderGroups(completed?.Orders, planned?.Orders),
                };
            });

            Promise.all(
                mergedEmployees.map(async (emp) => {
                    const detailedOrders = await fetchOrderDetails(emp.Orders || []);
                    return { ...emp, Orders: detailedOrders };
                })
            ).then((enrichedEmployees) => {
                setSelectedEvent((prev) => ({ ...prev, employees: enrichedEmployees }));
                setOrdersLoading(false);
            });
        } else {
            const mergedOrders = mergeOrderGroups(
                info.event.extendedProps?.orders,
                plannedRecord?.Orders
            );

            fetchOrderDetails(mergedOrders).then((detailedOrders) => {
                setSelectedEvent((prev) => ({ ...prev, orders: detailedOrders }));
                setOrdersLoading(false);
            });
        }
    };

    const handleCloseOffcanvas = () => {
        setShowOffcanvas(false);

        const selectedEls = document.querySelectorAll(".fc-event");
        selectedEls.forEach(el => el.classList.remove("fc-event-selected"));
    };

    const formatDuration = (value) => {
        if (value === null || value === undefined) return "0h 00m";

        if (typeof value === "string" && value.includes(":")) {
            const [h, m] = value.split(":").map(Number);
            return `${h}${t("hours")} ${m}m`;
        }

        if (typeof value === "number" && !Number.isNaN(value)) {
            const totalMinutes = Math.round(value * 60);
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            return `${hours}${t("hours")} ${minutes}m`;
        }

        const totalMinutes = parseInt(value, 10);
        if (!isNaN(totalMinutes)) {
            const h = Math.floor(totalMinutes / 60);
            const m = totalMinutes % 60;
            return `${h}${t("hours")} ${m}m`;
        }

        return String(value);
    };

    const formatHours = (decimalHours) => {
        if (!decimalHours) return "00:00";
        const hours = Math.floor(decimalHours);
        const minutes = Math.round((decimalHours - hours) * 60);
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    };

    const renderEventContent = (arg) => {
        const props = arg.event.extendedProps || {};
        const worked = Number(props.workedHours) || 0;
        const isOnLeave = props.isOnLeave || false;
        const deductionHours = Number(props.leaveDeductionHours) || 0;
        const dateStr = moment(arg.event.start).format("YYYY-MM-DD");

        let planned = 0;
        if (isEmployeeViewToggle) {
            const employeeId = props.employeeId;
            planned = employeePlannedMap[`${employeeId}-${dateStr}`]?.PlannedHours || 0;
        } else {
            const clientId = props.clientId;
            planned = clientPlannedMap[`${clientId}-${dateStr}`]?.PlannedHours || 0;
        }

        let badgeClass;
        let displayHours;

        if (isOnLeave) {
            badgeClass   = "bg-danger-subtle text-danger border border-danger border-opacity-25";
            displayHours = deductionHours;
        } else if (worked > 0 && planned > 0) {
            badgeClass   = "bg-primary-subtle text-primary";
            displayHours = worked + planned;
        } else if (worked > 0) {
            badgeClass   = "bg-success-subtle text-success";
            displayHours = worked;
        } else if (planned > 0) {
            badgeClass   = "bg-primary-subtle text-primary";
            displayHours = planned;
        } else {
            badgeClass   = "bg-secondary-subtle text-secondary border-0 disabled";
            displayHours = 0;
        }

        return (
            <div className="fc-event-custom-wrapper position-relative w-100 h-100 d-flex align-items-center justify-content-center px-1">
                <div className={`badge text-center hourRegistratinEvent ${badgeClass}`}>
                    {formatHours(displayHours)}
                </div>
            </div>
        );
    };

    const getFormattedCalendarTitle = (view, t) => {
        if (!view) return "";
        const viewType = view.type;
        if (viewType === "timeGridWeek" || viewType === "resourceTimelineWeek") {
            const start = moment(view.currentStart);
            const end = moment(view.currentEnd).subtract(1, 'day');
            const startMonth = t(`shortMonths.${start.format("MMMM").toLowerCase()}`);
            const endMonth = t(`shortMonths.${end.format("MMMM").toLowerCase()}`);
            return `${start.date()} ${startMonth} - ${end.date()} ${endMonth} ${end.year()}`;
        }

        if (viewType === "resourceTimelineMonth") {
            const date = moment(view.currentStart);
            const month = t(`months.${date.format("MMMM").toLowerCase()}`);
            return `${month} ${date.year()}`;
        }
        return view.title;
    };

    useEffect(() => {
        const checkLocationChange = () => {
            const storedId = localStorage.getItem("LocationId");
            if (storedId !== locationId) {
                setLocationId(storedId);
            }
        };
        const interval = setInterval(checkLocationChange, 1000);
        return () => clearInterval(interval);
    }, [locationId]);

    useEffect(() => {
        const api = calendarRef.current?.getApi?.();
        if (api) api.updateSize();
    }, [resourceList, initialEvents, currentView]);

    useEffect(() => {
        if (resourceList.length === 1 && resourceList[0].id === "no-data") {
            setResourceList([
                {
                    id: "no-data",
                    title: t("noDataMonth"),
                    eventColor: "transparent",
                },
            ]);
        }
    }, [i18n.language]);

    useEffect(() => {
        if (!dateRangeFrom || !dateRangeTo) return;
        if (employeesLoading || !employeeList.length) return;

        const calendarApiInstance = calendarRef.current?.getApi?.();
        if (!calendarApiInstance) return;

        const inclusiveEnd = moment(dateRangeTo).add(1, "day").toDate();

        setTimeout(() => {
            calendarApiInstance.changeView('resourceTimelineRange', {
                start: moment(dateRangeFrom).format("YYYY-MM-DD"),
                end: moment(inclusiveEnd).format("YYYY-MM-DD"),
            });
        }, 0);

        setIsRangeActive(true);
        setVisibleRange({
            start: moment(dateRangeFrom).format("YYYY-MM-DD"),
            end: moment(inclusiveEnd).format("YYYY-MM-DD"),
        });

        setTitle(
            `${moment(dateRangeFrom).format("DD MMM YYYY")} - ${moment(dateRangeTo).format("DD MMM YYYY")}`
        );

        (async () => {
            try {
                const freshLeaveMap = await fetchUnavailability(dateRangeFrom, inclusiveEnd);

                const primaryFetch = !isEmployeeViewToggle
                    ? fetchClientTotalHours(dateRangeFrom, inclusiveEnd)
                    : fetchHourRegistrations(dateRangeFrom, inclusiveEnd, freshLeaveMap);

                await Promise.all([primaryFetch, fetchPlannedHours(dateRangeFrom, inclusiveEnd)]);
            } catch (err) {
                console.error("Error during date-range fetch:", err);
            }
        })();

    }, [dateRangeFrom, dateRangeTo, employeeList, isEmployeeViewToggle]);

    const getColorFromName = (name) => {
        const colors = [
            "bg-primary-subtle text-primary-emphasis",
            "bg-success-subtle text-success-emphasis",
            "bg-warning-subtle text-warning-emphasis",
            "bg-danger-subtle text-danger-emphasis",
            "bg-info-subtle text-info-emphasis",
        ];
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % colors.length;
        return colors[index];
    };

    const formatHoursVerbose = (decimalHours) => {
        if (!decimalHours || isNaN(decimalHours)) return "00:00";
        const hours = Math.floor(decimalHours);
        const minutes = Math.round((decimalHours - hours) * 60);
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    };

    const measurementCanvasRef = useRef(null);
    const getMeasurementContext = () => {
        if (!measurementCanvasRef.current) {
            measurementCanvasRef.current = document.createElement("canvas");
        }
        return measurementCanvasRef.current.getContext("2d");
    };
    const getMaxNameWidth = (list) => {
        if (!list || list.length === 0) return 160;
        const ctx = getMeasurementContext();

        ctx.font = "600 13px system-ui, -apple-system, sans-serif";
        const maxNameWidth = Math.max(...list.map(r => ctx.measureText(r.title || "").width));

        ctx.font = "400 13px system-ui, -apple-system, sans-serif";
        const maxHoursWidth = Math.max(
            ...list.map(r => {
                const worked = r.extendedProps?.workedHours || 0;
                const expected = r.extendedProps?.expectedHours || 0;
                const hoursText = `${String(Math.floor(worked)).padStart(2, "0")}:00 / ${String(Math.floor(expected)).padStart(2, "0")}:00`;
                return ctx.measureText(hoursText).width;
            })
        );

        const maxTextWidth = Math.max(maxNameWidth, maxHoursWidth);
        return Math.min(Math.max(Math.ceil(maxTextWidth) + 72, 160), 380);
    };

    const maxNameWidth = useMemo(
        () => getMaxNameWidth(resourceList),
        [resourceList, isEmployeeViewToggle]
    );

    const resourceAreaColumns = [
        {
            field: "title",
            headerContent: isEmployeeViewToggle ? t("employee") : t("client"),
            isMain: true,
            width: `${maxNameWidth}px`,
            cellContent: function (arg) {
                const resourceId = arg.resource.id;
                const fullResource = resourceList.find((r) => r.id === resourceId);
                if (!fullResource) return null;

                const { title, extendedProps = {} } = fullResource;

                if (resourceId === "no-data") {
                    return {
                        html: `
                        <div class="d-flex align-items-center w-100">
                            <p class="calender-text fw-semibold mb-0"
                                style="white-space: normal; word-break: break-word;">
                                ${title}
                            </p>
                        </div>
                    `,
                    };
                }

                const avatarUrl = extendedProps.avatar;
                const initials = title
                    .split(" ")
                    .map((word) => word.charAt(0))
                    .join("")
                    .substring(0, 2)
                    .toUpperCase();

                const worked = extendedProps.workedHours || 0;
                const expected = extendedProps.expectedHours || 0;

                const colorClass = getColorFromName(title);
                const avatarHTML = avatarUrl
                    ? `<img src="${avatarUrl}" class="rounded-circle avatar avatar-xl object-fit-cover" alt="${title}" />`
                    : `<div class="avatar-name rounded-circle ${colorClass} fs-9"><span>${initials}</span></div>`;

                const hoursLine = isEmployeeViewToggle
                    ? `<p class="calender-text mb-0">${formatHoursVerbose(worked)} / ${formatHoursVerbose(expected)}</p>`
                    : `<p class="calender-text mb-0">${formatHoursVerbose(worked)}</p>`;

                return {
                    html: `
        <div class="d-flex align-items-center w-100 gap-2">
            <div class="avatar avatar-xl flex-shrink-0">${avatarHTML}</div>
            <div class="flex-grow-1 text-start" style="min-width: 0;">
                <p class="calender-text fw-semibold mb-0"
                    style="white-space: normal; word-break: break-word;"
                    title="${title}">${title}</p>
                ${hoursLine}
            </div>
        </div>
    `,
                };
            },
        },
        {
            field: "workedHours",
            headerContent: t("workedHours"),
            width: isEmployeeViewToggle ? "85px" : "90px",
            cellContent: (arg) => {
                const res = resourceList.find((r) => r.id === arg.resource.id);
                const worked = res?.extendedProps?.workedHours ?? 0;
                return { html: `<div class="calender-text w-100 mt-2 d-flex justify-content-center">${formatHoursVerbose(worked)}</div>` };
            },
        },
        ...(!isEmployeeViewToggle ? [] : [
            {
                field: "fixedRate",
                headerContent: t("hourlyRate"),
                width: "85px",
                cellContent: (arg) => {
                    const res = resourceList.find((r) => r.id === arg.resource.id);
                    const rate = res?.extendedProps?.fixedRate ?? 0;
                    return { html: `<div class="calender-text w-100 mt-2 d-flex justify-content-center">${currencySymbol}${rate.toFixed(2)}</div>`};
                },
            },
            {
                field: "grossTotal",
                headerContent: t("grossTotal"),
                width: "85px",
                cellContent: (arg) => {
                    const res = resourceList.find((r) => r.id === arg.resource.id);
                    const worked = res?.extendedProps?.workedHours ?? 0;
                    const rate = res?.extendedProps?.fixedRate ?? 0;
                    return { html: `<div class="calender-text w-100 mt-2 d-flex justify-content-center">${currencySymbol}${(worked * rate).toFixed(2)}</div>`};
                },
            },
        ]),
    ];

    const handleDatesSet = (arg) => {
        setTitle(getFormattedCalendarTitle(arg.view, t));

        if (isRangeActive) return;

        const { start, end } = arg.view.currentStart && arg.view.currentEnd
            ? { start: arg.view.currentStart, end: arg.view.currentEnd }
            : arg;

        const prev = previousRange.current;
        const isSameRange =
            prev.start?.toISOString() === start.toISOString() &&
            prev.end?.toISOString() === end.toISOString();

        if (!isSameRange) {
            previousRange.current = { start, end };

            (async () => {
                const freshLeaveMap = await fetchUnavailability(start, end);   

                if (!isEmployeeViewToggle) {
                    fetchClientTotalHours(start, end);
                } else {
                    fetchHourRegistrations(start, end, freshLeaveMap);          
                }
                fetchPlannedHours(start, end);
            })();
        }
    };

    useEffect(() => {
        fetchRoleAndClaims({
            navigate,
            claimType: "reports",
            claimValue: "view",
            setHasAccess,
            setIsLoading,
        });
    }, [navigate]);

    const loadingOrRedirect = handleLoadingAndAccess(
        isLoading,
        hasAccess
    );

    const handleViewToggle = (showEmployees) => {
        setIsEmployeeViewToggle(showEmployees);
        setIsRangeActive(false);

        const api = calendarRef.current?.getApi?.();
        const { currentStart, currentEnd } = api?.view || {};

        if (!showEmployees) {
            fetchClientTotalHours(currentStart, currentEnd);
        } else {
            if (api) fetchHourRegistrations(currentStart, currentEnd);
        }
    };

    const viewName = [
        t('calendar.monthView'),
        t('calendar.weekView')
    ];

    const handleFilter = (filter) => {
        setCurrentFilter(filter);

        const calendarApi = calendarRef.current?.getApi?.();
        if (!calendarApi) {
            console.error("calendarApi not available");
            return;
        }

        if (filter === t('calendar.monthView')) {
            setShowHoursHeader(false);
            calendarApi.changeView('resourceTimelineMonth');
        } else if (filter === t('calendar.weekView')) {
            setShowHoursHeader(true);
            calendarApi.changeView('resourceTimelineWeek');
        }

        setTimeout(() => syncDatePickersToView(calendarRef.current?.getApi?.()), 0);
    };

    useEffect(() => {
        const api = calendarRef.current?.getApi?.();
        if (!api) return;

        const lang = i18n.language.split('-')[0];
        moment.locale(lang);

        api.setOption("locale", lang);
        moment.locale(lang);
        setCalendarApi(api);

        const currentDate = api.getDate();
        const newView = showHoursHeader ? 'resourceTimelineWeek' : 'resourceTimelineMonth';
        setTimeout(() => {
            api.changeView(newView);
            api.gotoDate(currentDate);
        }, 0);

        const label = showHoursHeader ? t('calendar.weekView') : t('calendar.monthView');
        setCurrentFilter(label);

        setTimeout(() => {
            const view = api.view;
            if (view) {
                setTitle(getFormattedCalendarTitle(view, t));
                setCurrentView(view.type);
                handleDatesSet({ view });
                syncDatePickersToView(api);
            }
        }, 0);
    }, [i18n.language, showHoursHeader, t]);

    const getResourceAreaWidth = () => {
        if (!isEmployeeViewToggle) {
            return maxNameWidth + 90 + 10;  
        }
        return maxNameWidth + 85 + 85 + 85;
    };

    useEffect(() => {
        const handleResize = () => {
            const calendarApi = calendarRef.current?.getApi();
            if (calendarApi) {
                calendarApi.setOption('resourceAreaWidth', getResourceAreaWidth());
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const api = calendarRef.current?.getApi?.();
        if (!api || !resourceList.length) return;
        requestAnimationFrame(() => {
            api.setOption('resourceAreaWidth', getResourceAreaWidth());
            api.updateSize();
            const current = api.view.type;
            api.changeView(current);
        }, 0);
    }, [resourceList, initialEvents]);

    if (loadingOrRedirect) {
        return loadingOrRedirect;
    }

    const EXPORT_COLORS = {
        worked:  { fill: "D9F8EB", text: "00A862" },
        planned: { fill: "D9E8FB", text: "2C7BE5" },
        leave:   { fill: "FAD7DD", text: "E63757" },
        none:    { fill: "E9ECEF", text: "6C757D" },
    };
    const hexToRgb = (h) => [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16));
    const monthName = (m, short = false) =>
        t(`${short ? "shortMonths" : "months"}.${m.clone().locale("en").format("MMMM").toLowerCase()}`);



    // ── CHANGED: exportToExcel now accepts isEmployeeView to set correct first column header ──
    const exportToExcel = ({ title, fileName, rows, days, t, isEmployeeView }) => {
        if (!Array.isArray(rows) || rows.length === 0) return;

        const dayKeys = (days && days.length)
            ? days
            : Array.from(
                { length: moment().daysInMonth() },
                (_, i) => (i + 1).toString().padStart(2, '0')
            );

        const internalHeader = [
            "Name",
            "Work Hours",
            "Hourly Rate",
            "Total Gross",
            ...dayKeys
        ];

        const displayHeader = [
            isEmployeeView ? t("export.employeeName") : t("client"),
            t("export.workHours"),
            t("export.hourlyRate"),
            t("export.totalGross"),
            ...dayKeys.map(day => day.includes("-") ? day.split("-")[0] : day)
        ];

        const createStyledCell = (value, { bold = false, fill, align = 'center', color = '000000' } = {}) => ({
            v: value,
            t: typeof value === 'number' ? 'n' : 's',
            s: {
                font: { bold, color: { rgb: color }, sz: 12 },
                alignment: { horizontal: align, vertical: 'center' },
                fill: fill ? { fgColor: { rgb: fill } } : undefined,
                border: {
                    top: { style: 'thin', color: { rgb: '000000' } },
                    bottom: { style: 'thin', color: { rgb: '000000' } },
                    left: { style: 'thin', color: { rgb: '000000' } },
                    right: { style: 'thin', color: { rgb: '000000' } }
                },
            },
        });

        const sheetData = [];

        sheetData.push([
            createStyledCell(title, { bold: true, align: 'left', color: '000000' })
        ]);

        sheetData.push(displayHeader.map(h =>
            createStyledCell(h, { bold: true, fill: 'D9E1F2', align: 'center' })
        ));

        rows.forEach(row => {
            const styledRow = internalHeader.map((key, idx) => {
                const value = row[key] ?? '';
                const translatedKey = displayHeader[idx];
                const isName = idx === 0;
                const status = idx >= 4 ? (row._status?.[key] || "none") : null;
                const c = status ? EXPORT_COLORS[status] : null;
                return createStyledCell(value, {
                    align: isName ? 'left' : 'center',
                    fill: c?.fill,
                    color: c?.text || '000000',
                });
            });
            sheetData.push(styledRow);
        });

        const worksheet = utils.aoa_to_sheet(sheetData);
        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, toSafeSheetName(t("export.hourRegistration"), "Hours"));

        const filename = `${toSafeFileName(fileName || title)}.xlsx`;
        writeFile(workbook, filename);
    };

    const formatCurrency = (value) => {
        const num = parseFloat(value);
        return `${isNaN(num) ? '0.00' : num.toFixed(2)}`;
    };

    // ── CHANGED: exportToPDF now accepts isEmployeeView to set correct first column header ──
    const exportToPDF = async({ title, fileName, rows, days, t, dateRangeFrom, isEmployeeView }) => {
        if (!Array.isArray(rows) || rows.length === 0) return;

        const dayKeys = (days && days.length)
            ? days
            : Array.from(
                { length: moment().daysInMonth() },
                (_, i) => (i + 1).toString().padStart(2, '0')
            );

        const doc = new jsPDF("landscape", "pt", "A4");
        if (currencySymbol === "₹") await registerRupeeFont(doc);

        const monthGroups = {};
        dayKeys.forEach((day) => {
            const monthKey = day.includes("-") ? day.split("-")[1] : "same";
            if (!monthGroups[monthKey]) monthGroups[monthKey] = [];
            monthGroups[monthKey].push(day);
        });

        const monthGroupEntries = Object.entries(monthGroups);
        const isMultiPage = monthGroupEntries.length > 1;

        monthGroupEntries.forEach(([monthKey, monthDays], pageIndex) => {
            if (pageIndex > 0) doc.addPage();

            let pageTitle = title;
            if (isMultiPage) {
                const [dd, mm] = monthDays[0].split("-");
                const fromYear = moment(dateRangeFrom).year();
                const fromMonth = parseInt(moment(dateRangeFrom).format("MM"));
                const actualYear = parseInt(mm) < fromMonth ? fromYear + 1 : fromYear;
                const monthLabel = `${monthName(moment(`${actualYear}-${mm}-01`))} ${actualYear}`;
                pageTitle = `${title} — ${monthLabel}`;
            }

            const firstColHeader = isEmployeeView ? t("export.employeeName") : t("client");
            const fixedHeaders = [
                firstColHeader,
                t("export.workHours"),
                t("export.hourlyRate"),
                t("export.totalGross"),
            ];
            const fixedKeys = ["Name", "Work Hours", "Hourly Rate", "Total Gross"];

            const displayHeader = [...fixedHeaders, ...monthDays.map(day =>
                day.includes("-") ? day.split("-")[0] : day
            )];
            const internalKeys = [...fixedKeys, ...monthDays];

            const body = rows.map(row => internalKeys.map(key => row[key] ?? ""));
            const isWeekView = monthDays.length <= 7;

            drawPdfText(doc, pageTitle, 40, 40, 16, true);
            const baseHooks = getUnicodePdfHooks(doc, getRupeeCellHooks(doc, [2, 3]));

            autoTable(doc, {
                head: [displayHeader],
                body,
                startY: 60,
                tableWidth: "100%",
                styles: {
                    fontSize: isWeekView ? 9 : 5,
                    cellPadding: 2,
                    halign: "center",
                    valign: "middle",
                    overflow: "hidden",
                    lineColor: [0, 0, 0],
                    lineWidth: 0.1,
                },
                headStyles: {
                    fillColor: [217, 225, 242],
                    textColor: [0, 0, 0],
                    halign: "center",
                    fontStyle: "bold",
                    fontSize: isWeekView ? 9 : 5,
                    lineColor: [0, 0, 0],
                    lineWidth: 0.1,
                },
                columnStyles: {
                    0: { halign: "left" },
                    1: { halign: "center" },
                    2: { halign: "center", minCellWidth: isWeekView ? 60 : 38 },
                    3: { halign: "center", minCellWidth: isWeekView ? 60 : 38 },
                },
                theme: "grid",
                ...baseHooks,
                    didParseCell: (data) => {
                        baseHooks?.didParseCell?.(data);
                        if (data.section !== "body" || data.column.index < 4) return;
                        const key = internalKeys[data.column.index];
                        const status = rows[data.row.index]?._status?.[key] || "none";
                        const c = EXPORT_COLORS[status];
                        data.cell.styles.fillColor = hexToRgb(c.fill);
                        data.cell.styles.textColor = hexToRgb(c.text);
                    },
            });
        });

        const filename = `${toSafeFileName(fileName || title)}.pdf`;
        doc.save(filename);
    };

    const groupEventsByEmployee = (events, resourceList) => {
        const employeeMap = {};

        events.forEach(evt => {
            const resObj = evt.getResources?.()[0];
            const employeeId = resObj?.id;
            const employee = resObj?.title || "Unknown";

            let workedRaw = evt.extendedProps?.workedHours;
            if (workedRaw == null) {
                const res = resourceList.find(r => r.id === employeeId);
                workedRaw = res?.extendedProps?.workedHours ?? 0;
            }

            const res = resourceList.find(r => r.id === employeeId);
            const hourlyRate = res?.extendedProps?.fixedRate ?? 0;
            const gross = workedRaw * hourlyRate;
            const date = moment(evt.start).format("DD-MM");

            const dateKey = moment(evt.start).format("YYYY-MM-DD");
            const plannedRaw =
            employeePlannedMap[`${evt.extendedProps?.employeeId ?? employeeId}-${dateKey}`]
                ?.PlannedHours || 0;
            const isOnLeave = evt.extendedProps?.isOnLeave || false;
            const leaveHours = Number(evt.extendedProps?.leaveDeductionHours) || 0;
            const dayHours = isOnLeave ? leaveHours : workedRaw + plannedRaw;

            if (!employeeMap[employee]) {
                employeeMap[employee] = {
                    "Name": employee,
                    "Work Hours": formatHoursVerbose(workedRaw),
                    "Hourly Rate": `${currencySymbol}${hourlyRate.toFixed(2)}`,
                    "Total Gross": `${currencySymbol}${gross.toFixed(2)}`,
                    "TotalMinutes": workedRaw,
                    "TotalGrossRaw": gross,
                    dailyBreakdown: {},
                };
            } else {
                employeeMap[employee]["TotalMinutes"] += workedRaw;
                employeeMap[employee]["TotalGrossRaw"] += gross;
                employeeMap[employee]["Work Hours"] = formatHoursVerbose(employeeMap[employee]["TotalMinutes"]);
                employeeMap[employee]["Total Gross"] = `${currencySymbol}${formatCurrency(employeeMap[employee]["TotalGrossRaw"])}`;
            }

            const em = employeeMap[employee];
            em.dailyRaw = em.dailyRaw || {};
            em.dailyStatus = em.dailyStatus || {};
            em.dailyRaw[date] = (em.dailyRaw[date] || 0) + dayHours;
            em.dailyBreakdown[date] = formatHoursVerbose(em.dailyRaw[date]);
            if (isOnLeave) em.dailyStatus[date] = "leave";
            else if (plannedRaw > 0) em.dailyStatus[date] = "planned";
            else if (workedRaw > 0 && em.dailyStatus[date] !== "planned") em.dailyStatus[date] = "worked";
        });

        return employeeMap;
    };

    // ── NEW: groupEventsByClient — mirrors groupEventsByEmployee for client view ──
    const groupEventsByClient = (events, resourceList) => {
        const clientMap = {};

        events.forEach(evt => {
            const resObj = evt.getResources?.()[0];
            const clientName = resObj?.title || evt.extendedProps?.clientName || "Unknown";
            const workedRaw = Number(evt.extendedProps?.workedHours) || 0;
            const date = moment(evt.start).format("DD-MM");
            const dateKey = moment(evt.start).format("YYYY-MM-DD");
            const plannedRaw =
                clientPlannedMap[`${evt.extendedProps?.clientId}-${dateKey}`]?.PlannedHours || 0;
            const dayHours = workedRaw + plannedRaw;

            if (!clientMap[clientName]) {
                clientMap[clientName] = {
                    "Name": clientName,
                    "Work Hours": formatHoursVerbose(workedRaw),
                    "Hourly Rate": `${currencySymbol}0.00`,
                    "Total Gross": `${currencySymbol}0.00`,
                    "TotalMinutes": workedRaw,
                    dailyBreakdown: {},
                };
            } else {
                clientMap[clientName]["TotalMinutes"] += workedRaw;
                clientMap[clientName]["Work Hours"] = formatHoursVerbose(clientMap[clientName]["TotalMinutes"]);
            }

            const cm = clientMap[clientName];
            cm.dailyRaw = cm.dailyRaw || {};
            cm.dailyStatus = cm.dailyStatus || {};
            cm.dailyRaw[date] = (cm.dailyRaw[date] || 0) + dayHours;
            cm.dailyBreakdown[date] = formatHoursVerbose(cm.dailyRaw[date]);
            if (plannedRaw > 0) cm.dailyStatus[date] = "planned";
            else if (workedRaw > 0 && cm.dailyStatus[date] !== "planned") cm.dailyStatus[date] = "worked";
        });

        return clientMap;
    };

    const buildExportRows = (employeeMap, days) => {
        const dayKeys = (days && days.length)
            ? days
            : Array.from(
                { length: moment().daysInMonth() },
                (_, i) => (i + 1).toString().padStart(2, '0')
            );

        return Object.values(employeeMap).map(emp => {
            const breakdown = {};
            const status = {};
            const findByDay = (obj = {}, day) => {
                if (obj[day] !== undefined) return obj[day];
                const match = Object.keys(obj).find((k) => k.split("-")[0] === day);
                return match ? obj[match] : undefined;
            };

            dayKeys.forEach(day => {
                breakdown[day] = findByDay(emp.dailyBreakdown, day) || "00:00";
                status[day] = findByDay(emp.dailyStatus, day) || "none";
            });

            return {
                'Name': emp['Name'],
                ...breakdown,
                'Work Hours': emp['Work Hours'] || "00:00",
                'Hourly Rate': emp['Hourly Rate'] || `${currencySymbol}0.00`,
                'Total Gross': emp['Total Gross'] || `${currencySymbol}0.00`,
                _status: status,
            };
        });
    };

    // ── CHANGED: branches on isEmployeeViewToggle to use correct grouping fn and title ──
    const handleExportExcel = () => {
    try {
        if (loading || employeesLoading) return;
        const calendarApi = calendarRef.current?.getApi?.();
        if (!calendarApi) return;

        const view = calendarApi.view;
        const start = moment(view.currentStart);
        const end = moment(view.currentEnd);

        const events = calendarApi.getEvents().filter(
            e => moment(e.start).isSameOrAfter(start) && moment(e.start).isBefore(end)
        );

        // ── CHANGED: pick grouping function based on active view ──
        const dataMap = isEmployeeViewToggle
            ? groupEventsByEmployee(events, resourceList)
            : groupEventsByClient(events, resourceList);

        let days = [];
        let title = "";
        const exportLabel = isEmployeeViewToggle ? t("HoursRegistration") : t("clients");

        if (isRangeActive && dateRangeFrom && dateRangeTo) {
            let d = moment(dateRangeFrom);
            const rangeEnd = moment(dateRangeTo);
            while (d.isSameOrBefore(rangeEnd)) {
                days.push(d.format("DD-MM"));
                d.add(1, "day");
            }
            const fromMoment = moment(dateRangeFrom);
            const toMoment = moment(dateRangeTo);
            const sameMonth =
                fromMoment.month() === toMoment.month() &&
                fromMoment.year() === toMoment.year();
            title = sameMonth
                ? `${exportLabel} - ${monthName(fromMoment)} ${fromMoment.date()} – ${toMoment.date()}, ${toMoment.year()}`
                : `${exportLabel} - ${monthName(fromMoment)} ${fromMoment.date()} – ${monthName(toMoment)} ${toMoment.date()}, ${toMoment.year()}`;
        } else if (view.type === "resourceTimelineWeek") {
            days = Array.from({ length: end.diff(start, "days") }, (_, i) =>
                start.clone().add(i, "days").format("DD")
            );
            const weekEnd = end.clone().subtract(1, "day");
            title = t("export.weekTitle", {
                start: `${start.format("DD")} ${monthName(start, true)} ${start.year()}`,
                end: `${weekEnd.format("DD")} ${monthName(weekEnd, true)} ${weekEnd.year()}`,
            });
        } else {
            days = Array.from({ length: start.daysInMonth() }, (_, i) =>
                start.clone().date(i + 1).format("DD")
            );
            title = t("export.monthTitle", {
                month: monthName(start),
                year: start.year(),
            });
        }

        const fileName = buildExportFileName({
            label: exportLabel,
            from: isRangeActive && dateRangeFrom ? dateRangeFrom : start.toDate(),
            to: isRangeActive && dateRangeTo ? dateRangeTo : end.clone().subtract(1, "day").toDate(),
        });

        const rows = buildExportRows(dataMap, days);
        exportToExcel({ title, fileName, rows, days, t, isEmployeeView: isEmployeeViewToggle });
    } catch (err) {
        console.error("Excel export failed:", err);
        toast.error(t("somethingWentWrong"), { position: "top-right", autoClose: 5000 });
        }
    };

    const handleExportPDF = () => {
    try{
        if (loading || employeesLoading) return;
        const calendarApi = calendarRef.current?.getApi?.();
        if (!calendarApi) return;

        const view = calendarApi.view;
        const start = moment(view.currentStart);
        const end = moment(view.currentEnd);

        const events = calendarApi.getEvents().filter(
            e => moment(e.start).isSameOrAfter(start) && moment(e.start).isBefore(end)
        );

        // ── CHANGED: pick grouping function based on active view ──
        const dataMap = isEmployeeViewToggle
            ? groupEventsByEmployee(events, resourceList)
            : groupEventsByClient(events, resourceList);

        let days = [];
        let title = "";
        const exportLabel = isEmployeeViewToggle ? t("HoursRegistration") : t("clients");

        if (isRangeActive && dateRangeFrom && dateRangeTo) {
            let d = moment(dateRangeFrom);
            const rangeEnd = moment(dateRangeTo);
            while (d.isSameOrBefore(rangeEnd)) {
                days.push(d.format("DD-MM"));
                d.add(1, "day");
            }
            const fromMoment = moment(dateRangeFrom);
            const toMoment = moment(dateRangeTo);
            const sameMonth =
                fromMoment.month() === toMoment.month() &&
                fromMoment.year() === toMoment.year();
            title = sameMonth
                ? `${exportLabel} - ${monthName(fromMoment)} ${fromMoment.date()} – ${toMoment.date()}, ${toMoment.year()}`
                : `${exportLabel} - ${monthName(fromMoment)} ${fromMoment.date()} – ${monthName(toMoment)} ${toMoment.date()}, ${toMoment.year()}`;
        } else if (view.type === "resourceTimelineWeek") {
            let d = start.clone();
            while (d.isBefore(end)) {
                days.push(d.format("DD"));
                d.add(1, "day");
            }
            const endMinus1 = end.clone().subtract(1, "day");
            title = t("export.weekTitle", {
                start: `${start.format("DD")} ${monthName(start, true)} ${start.year()}`,
                end: `${endMinus1.format("DD")} ${monthName(endMinus1, true)} ${endMinus1.year()}`,
            });
        } else {
            const monthLen = start.daysInMonth();
            days = Array.from({ length: monthLen }, (_, i) =>
                start.clone().date(i + 1).format("DD")
            );
            title = t("export.monthTitle", {
                month: monthName(start),
                year: start.year(),
            });
        }

            const fileName = buildExportFileName({
                label: exportLabel,
                from: isRangeActive && dateRangeFrom ? dateRangeFrom : start.toDate(),
                to: isRangeActive && dateRangeTo ? dateRangeTo : end.clone().subtract(1, "day").toDate(),
            });

            const rows = buildExportRows(dataMap, days);
            exportToPDF({ title, fileName, rows, days, t, dateRangeFrom, isEmployeeView: isEmployeeViewToggle })
            .catch((err) => {
                console.error("PDF export failed:", err);
                toast.error(t("somethingWentWrong"), { position: "top-right", autoClose: 5000 });
            });
        } catch (err) {
            console.error("PDF export failed:", err);
            toast.error(t("somethingWentWrong"), { position: "top-right", autoClose: 5000 });
        }
    };

    const handleViewDidMount = (viewInfo) => {
        const calendarEl = viewInfo.el;

        const headerScroller = calendarEl.querySelector('.fc-scrollgrid-section-header .fc-scroller');
        if (headerScroller) {
            headerScroller.style.overflow = 'visible';
            headerScroller.style.marginBottom = '0';
        }

        const bodyScroller = calendarEl.querySelector('.fc-scrollgrid-section-body .fc-scroller');
        if (bodyScroller) {
            bodyScroller.style.overflowX = 'auto';
            bodyScroller.style.overflowY = 'hidden';
        }
    };

    const isInvoiceFormValid =
        selectedEntity &&
        invoiceFrom &&
        invoiceTo;

    return (
        <>
            <Row className="g-3 mb-3">
                <Col lg={12} xxl={12}>
                    <Card className="mb-3 h-100">
                        <Background
                            image={corner4}
                            className="bg-card"
                            style={{
                                borderTopRightRadius: "0.375rem",
                                borderBottomRightRadius: "0.375rem",
                            }}
                        />
                        <Card.Body className="position-relative pe-4 pb-2">
                            <Row className="flex-between-center">
                                <Col md>
                                    <h5 className="mb-2 mb-md-0">{t("HoursRegistration")}</h5>
                                </Col>

                                <Col xs="auto" className="d-flex align-items-center gap-2">

                                    {/* Toggle */}
                                    <div className="d-inline-flex align-items-center rounded-pill p-1 bg-custom-light border gap-2px">
                                        <button
                                            type="button"
                                            onClick={() => handleViewToggle(true)}
                                            className={`border-0 rounded-pill px-2 py-0 fw-medium font-size-13 ${isEmployeeViewToggle ? "toggle-btn-selected" : "bg-transparent text-secondary"
                                                }`}
                                        >
                                            {t("employees")}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => handleViewToggle(false)}
                                            className={`border-0 rounded-pill px-2 py-0 fw-medium font-size-13 ${!isEmployeeViewToggle ? "toggle-btn-selected" : "bg-transparent text-secondary"
                                                }`}
                                        >
                                            {t("clients")}
                                        </button>
                                    </div>

                                    {/* Generate Invoice Button */}
                                    {!isEmployeeViewToggle && (
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm"
                                            onClick={handleGenerateInvoice}
                                        >
                                            {t("generateInvoice")}
                                        </button>
                                    )}
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row className="g-3 position-relative">
                <Col lg={12} xl={12}>
                    <Card className="d-flex flex-column">
                        <Card.Header>
                            <Row className="align-items-center gx-3">
                                <Col className="d-flex align-items-center justify-content-start gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip className="position-fixed" id="prevTooltip">
                                                    {t("previous")}
                                                </Tooltip>
                                            }
                                        >
                                            <Button
                                                variant="link"
                                                className="icon-item icon-item-sm shadow-none p-0"
                                                onClick={() => {
                                                    setIsRangeActive(false);
                                                    const calendarApiInstance = calendarRef.current?.getApi?.();
                                                    const newView = showHoursHeader ? 'resourceTimelineWeek' : 'resourceTimelineMonth';
                                                    calendarApiInstance?.changeView(newView);
                                                    calendarApiInstance?.prev();

                                                    setTimeout(() => {
                                                        const view = calendarApiInstance?.view;
                                                        if (view) setTitle(getFormattedCalendarTitle(view, t));
                                                        syncDatePickersToView(calendarApiInstance);
                                                    }, 0);
                                                }}
                                            >
                                                <FontAwesomeIcon icon="arrow-left" />
                                            </Button>
                                        </OverlayTrigger>

                                        <div className="px-3 border rounded-3 d-flex align-items-center text-nowrap">
                                            <h6 className="py-2 text-600 font-size-13 mb-0">
                                                {title || (calendarRef.current?.getApi?.()?.view?.title ?? '')}
                                            </h6>
                                        </div>

                                        <OverlayTrigger
                                            placement="bottom"
                                            overlay={
                                                <Tooltip className="position-fixed" id="nextTooltip">
                                                    {t("nextLable")}
                                                </Tooltip>
                                            }
                                        >
                                            <Button
                                                variant="link"
                                                className="icon-item icon-item-sm shadow-none p-0"
                                                onClick={() => {
                                                    setIsRangeActive(false);
                                                    const calendarApiInstance = calendarRef.current?.getApi?.();
                                                    const newView = showHoursHeader ? 'resourceTimelineWeek' : 'resourceTimelineMonth';
                                                    calendarApiInstance?.changeView(newView);
                                                    calendarApiInstance?.next();

                                                    setTimeout(() => {
                                                        const view = calendarApiInstance?.view;
                                                        if (view) setTitle(getFormattedCalendarTitle(view, t));
                                                        syncDatePickersToView(calendarApiInstance);
                                                    }, 0);
                                                }}
                                            >
                                                <FontAwesomeIcon icon="arrow-right" />
                                            </Button>
                                        </OverlayTrigger>
                                    </div>
                                </Col>

                                <Col xs="auto" className="d-flex align-items-center gap-2">
                                    <div className="dateRange-custom-width position-relative">
                                        <DatePicker
                                            selected={dateRangeFrom}
                                            onChange={(date) => {
                                                setDateRangeFrom(date);
                                                setIsFromOpen(false);
                                                if (!date) handleClearDateRange();
                                            }}
                                            open={isFromOpen}
                                            onInputClick={() => setIsFromOpen(true)}
                                            onClickOutside={() => setIsFromOpen(false)}
                                            placeholderText={t("from") || "From"}
                                            className="form-control form-control-sm w-100 pe-4"
                                            dateFormat="dd-MM-yyyy"
                                            maxDate={dateRangeTo || undefined}
                                            popperPlacement="bottom-start"
                                        />
                                        <span
                                            className="position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 lh-1 cursor-pointer z-index-1"
                                            onClick={() => setIsFromOpen((prev) => !prev)}
                                        >
                                            <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                                        </span>
                                    </div>
                                    <span className="text-muted small">{t("to") || "to"}</span>
                                    <div className="dateRange-custom-width position-relative">
                                        <DatePicker
                                            selected={dateRangeTo}
                                            onChange={(date) => {
                                                setDateRangeTo(date);
                                                setIsToOpen(false);
                                                if (!date) handleClearDateRange();
                                            }}
                                            open={isToOpen}
                                            onInputClick={() => setIsToOpen(true)}
                                            onClickOutside={() => setIsToOpen(false)}
                                            minDate={dateRangeFrom || undefined}
                                            placeholderText={t("to") || "To"}
                                            className="form-control form-control-sm w-100 pe-4"
                                            dateFormat="dd-MM-yyyy"
                                            popperPlacement="bottom-start"
                                        />
                                        <span
                                            className="position-absolute top-50 end-0 translate-middle-y pe-2 fs-10 cursor-pointer z-index-1"
                                            onClick={() => setIsToOpen((prev) => !prev)}
                                        >
                                            <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" />
                                        </span>
                                    </div>
                                </Col>

                                <Col xs="auto" className="d-flex align-items-center justify-content-end gap-2">
                                    <DropdownFilter
                                        className="me-2"
                                        filters={viewName}
                                        currentFilter={currentFilter}
                                        handleFilter={handleFilter}
                                        icon="sort"
                                        right
                                    />
                                    <OverlayTrigger
                                        placement="bottom"
                                        overlay={<Tooltip id="excelTooltip">{t("exportExcel")}</Tooltip>}
                                    >
                                        <Button
                                            variant="outline-success"
                                            className="d-flex align-items-center gap-2 px-3 py-1 me-2 rounded shadow-sm border-success"
                                            onClick={handleExportExcel}
                                        >
                                            <FontAwesomeIcon icon={faFileExcel} style={{ fontSize: '1.30rem' }} />
                                        </Button>
                                    </OverlayTrigger>

                                    <OverlayTrigger
                                        placement="bottom"
                                        overlay={<Tooltip id="pdfTooltip">{t("exportPDF")}</Tooltip>}
                                    >
                                        <Button
                                            variant="outline-danger"
                                            className="d-flex align-items-center gap-2 px-3 py-1 rounded shadow-sm border-danger"
                                            onClick={handleExportPDF}
                                        >
                                            <FontAwesomeIcon icon={faFilePdf} style={{ fontSize: '1.30rem' }} />
                                        </Button>
                                    </OverlayTrigger>
                                </Col>
                            </Row>
                        </Card.Header>

                        <Card.Body className="p-0 position-relative">
                            {(loading || employeesLoading) && (
                                <div className="calendar-overlay">
                                    <Spinner animation="border" variant="secondary" />
                                </div>
                            )}

                            <div id="hoursCalendar">
                                <FullCalendar
                                    ref={calendarRef}
                                    headerToolbar={false}
                                    plugins={[resourceTimelinePlugin, timeGridPlugin]}
                                    resourceAreaWidth={getResourceAreaWidth()}
                                    resourceAreaColumns={resourceAreaColumns}
                                    stickyHeaderDates={true}
                                    stickyFooterScrollbar={true}
                                    slotMinWidth={120}
                                    initialView={'resourceTimelineMonth'}
                                    viewDidMount={handleViewDidMount}
                                    eventClick={(info) => {
                                        const worked = Number(info.event.extendedProps?.workedHours) || 0;
                                        const dateStr = moment(info.event.start).format("YYYY-MM-DD");

                                        let planned = 0;
                                        if (isEmployeeViewToggle) {
                                            const employeeId = info.event.extendedProps?.employeeId;
                                            planned = employeePlannedMap[`${employeeId}-${dateStr}`]?.PlannedHours || 0;
                                        } else {
                                            const clientId = info.event.extendedProps?.clientId;
                                            planned = clientPlannedMap[`${clientId}-${dateStr}`]?.PlannedHours || 0;
                                        }

                                        if (worked === 0 && planned === 0) {
                                            info.jsEvent.preventDefault();
                                            return;
                                        }

                                        handleEventClick(info);
                                    }}
                                    eventContent={renderEventContent}
                                    datesSet={handleDatesSet}

                                    views={{
                                        resourceTimelineMonth: {
                                            type: 'resourceTimeline',
                                            slotDuration: { days: 1 },
                                            slotLabelFormat: [{ day: '2-digit' }],
                                            slotMinWidth: 80
                                        },
                                        resourceTimelineWeek: {
                                            type: 'resourceTimeline',
                                            duration: { weeks: 1 },
                                            slotLabelFormat: [{ weekday: 'short', day: 'numeric' }],
                                            slotDuration: { days: 1 },
                                            slotMinWidth: 40
                                        },
                                        resourceTimelineRange: {
                                            type: 'resourceTimeline',
                                            slotDuration: { days: 1 },
                                            slotLabelFormat: [{ day: 'numeric', month: 'short' }],
                                            slotMinWidth: 60,
                                            slotLabelContent: (arg) => {
                                                const d = moment(arg.date);
                                                const monthKey = d.format("MMMM").toLowerCase();
                                                return `${d.date()} ${t(`shortMonths.${monthKey}`)}`;
                                            },
                                            visibleRange: visibleRange
                                                ? { start: visibleRange.start, end: visibleRange.end }
                                                : undefined,
                                        }
                                    }}
                                    eventMaxStack={true}
                                    themeSystem="bootstrap"
                                    resources={resourceTable.getRowModel().rows.map(r => r.original)}
                                    resourceOrder="sortIndex"
                                    events={initialEvents}
                                    timeZone="local"
                                    allDaySlot={false}
                                    locale={i18n.language.split('-')[0]}
                                    direction={isRTL ? 'rtl' : 'ltr'}
                                    contentHeight="auto"
                                    slotEventOverlap={true}
                                    firstDay={1}
                                    schedulerLicenseKey="GPL-My-Project-Is-Open-Source"
                                />
                            </div>
                        </Card.Body>
                        {resourceList.length > 0 && (
                            <Card.Footer>
                                <AdvanceTableProvider {...resourceTable}>
                                    <AdvanceTableFooter
                                        rowInfo
                                        navButtons
                                        rowsPerPageSelection
                                        tableKey="hoursResourceTable"
                                    />
                                </AdvanceTableProvider>
                            </Card.Footer>
                        )}     
                    </Card>
                </Col>
            </Row>

            <Offcanvas
                show={showOffcanvas}
                onHide={handleCloseOffcanvas}
                placement="end"
                backdrop={true}
                scroll={true}
                className="task-offcanvas"
            >
                <div className="thin-scrollbar h-100 d-flex flex-column">
                    {!selectedEvent ? (
                        <div className="text-center my-5">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">{t("loading")}</span>
                            </Spinner>
                        </div>
                    ) : (
                        <>
                            <Offcanvas.Header className="px-3 py-2 border-bottom" closeButton>
                                <div className="w-100">
                                    <Row className="align-items-center">
                                        <Col>
                                            <h5 className="fw-bold mb-2 mt-2 fs-7">
                                                {resourceList.find(r => r.id === selectedEvent?.resourceId)?.title || t("Event Details")}
                                            </h5>
                                            <p className="text-muted mb-0">
                                                <i className="fa fa-calendar-alt me-2 icon-opacity"></i>
                                                {(() => {
                                                    const d = new Date(selectedEvent.start);
                                                    const dayKey = d.toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
                                                    const monthKey = d.toLocaleDateString("en-US", { month: "long" }).toLowerCase();

                                                    return `${t(`fullDaysName.${dayKey}`)}, ${t(`months.${monthKey}`)} ${d.getDate()}, ${d.getFullYear()}`;
                                                })()}
                                            </p>
                                        </Col>
                                    </Row>
                                </div>
                            </Offcanvas.Header>
                            <Offcanvas.Body className="px-3">
                                {ordersLoading ? (
                                    <div className="text-center my-5">
                                        <Spinner animation="border" role="status" />
                                    </div>
                                ) : (
                                    <>
                                        {(selectedEvent.workedHours > 0 || selectedEvent.plannedHours > 0) && (
                                            <div className={`d-flex align-items-center justify-content-between p-3 mb-3 rounded shadow-sm text-white ${selectedEvent.plannedHours > 0 ? "bg-primary" : "bg-success"}`}>
                                                <div className="d-flex align-items-center">
                                                    <FontAwesomeIcon icon={faClock} className="me-3 fs-8" />
                                                    <div className="fw-bold">{t("totalHoursLable")}</div>
                                                </div>
                                                <div className="fw-bold fs-5 text-end">
                                                    {formatDuration(selectedEvent.isPlannedOnly ? selectedEvent.plannedHours : selectedEvent.workedHours)}
                                                </div>
                                            </div>
                                        )}

                                        {!isEmployeeViewToggle ? (
                                            <>
                                                <h5 className="fw-bold mb-3">{t("details")} :</h5>
                                                {!selectedEvent.employees?.length ? (
                                                    <p className="text-muted">{t("noOrdersFound")}</p>
                                                ) : (
                                                    <div className="d-flex flex-column gap-3">
                                                        {selectedEvent.employees.map((emp) => {
                                                            const empInfo = employeeList.find(e => String(e.UserId) === String(emp.EmployeeId));
                                                            const empName = empInfo?.Name || `Employee ${emp.EmployeeId}`;

                                                            return emp.Orders?.map((order) => (
                                                                <div key={`${emp.EmployeeId}-${order.OrderId}`} className="p-3 border rounded shadow-sm bg-light">
                                                                    <Row className="mb-2 align-items-center">
                                                                        <Col xs={1}><FontAwesomeIcon icon={faUser} className="me-2 icon-opacity" size="md" /></Col>
                                                                        <Col><p className="fw-semi-bold mb-1 fs-8">{empName}</p></Col>
                                                                    </Row>
                                                                    <Row className="mb-2 align-items-center">
                                                                        <Col xs={1}><FontAwesomeIcon icon={faClipboardList} className="me-2 mb-1 icon-opacity" size="md" /></Col>
                                                                        <Col><p className="fw-semi-bold mb-1 fs-8">{t("taskDetails")}:</p></Col>
                                                                    </Row>
                                                                    <div>
                                                                        {order.OrderDetails?.map((detail, idx) => (
                                                                            <div key={idx} className="mb-2">
                                                                                        <div>
                                                                                            {(() => {
                                                                                                let start = null, end = null, isPlanned = false;
                                                                                                const extractUtcTime = (timeStr) => moment.utc(timeStr).format("HH:mm:ss");

                                                                                                if (detail.ActualStartDate && detail.ActualStartTime) {
                                                                                                    start = moment.utc(`${detail.ActualStartDate}T${extractUtcTime(detail.ActualStartTime)}`).local().toDate();
                                                                                                }
                                                                                                if (detail.ActualEndDate && detail.ActualEndTime) {
                                                                                                    end = moment.utc(`${detail.ActualEndDate}T${extractUtcTime(detail.ActualEndTime)}`).local().toDate();
                                                                                                }
                                                                                                if (!start && detail.WeekStartDate && detail.StartTime) {
                                                                                                    start = moment.utc(`${moment(detail.WeekStartDate).format("YYYY-MM-DD")}T${extractUtcTime(detail.StartTime)}`).local().toDate();
                                                                                                    isPlanned = true;
                                                                                                }
                                                                                                if (!end && detail.WeekStartDate && detail.EndTime) {
                                                                                                    end = moment.utc(`${moment(detail.WeekStartDate).format("YYYY-MM-DD")}T${extractUtcTime(detail.EndTime)}`).local().toDate();
                                                                                                    isPlanned = true;
                                                                                                }

                                                                                                const fmt = (d) => d ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }) : "?";
                                                                                                let durationText = "0h 0m";
                                                                                                if (start && end) {
                                                                                                    let dec = (end - start) / 3600000;
                                                                                                    if (!isPlanned && detail.BreakTime > 0) dec -= detail.BreakTime / 60;
                                                                                                    durationText = formatDuration(Math.max(0, Math.round(dec * 100) / 100));
                                                                                                }

                                                                                                return (
                                                                                                    <>
                                                                                                        <div className="d-flex align-items-start justify-content-between mb-1 w-100">
                                                                                                            <p className="mb-0 fw-semibold me-2">{detail.TaskName}</p>
                                                                                                            <span className={`badge flex-shrink-0 ${isPlanned ? "bg-primary" : "bg-success"}`}>
                                                                                                                {isPlanned ? t("planned") : t("completed")}
                                                                                                            </span>
                                                                                                        </div>
                                                                                                        <p className="mb-1 text-muted small">
                                                                                                            {`${fmt(start)} – ${fmt(end)} | ${durationText}${!isPlanned && detail.BreakTime > 0 ? ` | Break: ${detail.BreakTime}m` : ""}`}
                                                                                                        </p>
                                                                                                    </>
                                                                                                );
                                                                                            })()}
                                                                                        </div>
                                                                                {idx < order.OrderDetails.length - 1 && <hr className="my-2" />}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ));
                                                        })}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <h5 className="fw-bold mb-3">{t("details")} :</h5>
                                                {!selectedEvent.orders?.length ? (
                                                    <p className="text-muted">{t("noOrdersFound")}</p>
                                                ) : (
                                                    <div className="d-flex flex-column gap-3">
                                                        {selectedEvent.orders.map((order) => (
                                                            <div key={order.OrderId} className="p-3 border rounded shadow-sm bg-light">
                                                                <Row className="mb-2 align-items-center">
                                                                    <Col xs={1}><FontAwesomeIcon icon={faUser} className="me-2 icon-opacity" size="md" /></Col>
                                                                    <Col><p className="fw-semi-bold mb-1 fs-8">{order.ClientName || "Unknown Client"}</p></Col>
                                                                </Row>
                                                                <Row className="mb-2 align-items-center">
                                                                    <Col xs={1}><FontAwesomeIcon icon={faClipboardList} className="me-2 mb-1 icon-opacity" size="md" /></Col>
                                                                    <Col><p className="fw-semi-bold mb-1 fs-8">{t("taskDetails")}:</p></Col>
                                                                </Row>
                                                                <div>
                                                                    {order.OrderDetails?.map((detail, idx) => (
                                                                        <div key={idx} className="mb-2">
                                                                                    <div>
                                                                                        {(() => {
                                                                                            let start = null, end = null, isPlanned = false;
                                                                                            const extractUtcTime = (timeStr) => moment.utc(timeStr).format("HH:mm:ss");

                                                                                            if (detail.ActualStartDate && detail.ActualStartTime) {
                                                                                                start = moment.utc(`${detail.ActualStartDate}T${extractUtcTime(detail.ActualStartTime)}`).local().toDate();
                                                                                            }
                                                                                            if (detail.ActualEndDate && detail.ActualEndTime) {
                                                                                                end = moment.utc(`${detail.ActualEndDate}T${extractUtcTime(detail.ActualEndTime)}`).local().toDate();
                                                                                            }
                                                                                            if (!start && detail.WeekStartDate && detail.StartTime) {
                                                                                                start = moment.utc(`${moment(detail.WeekStartDate).format("YYYY-MM-DD")}T${extractUtcTime(detail.StartTime)}`).local().toDate();
                                                                                                isPlanned = true;
                                                                                            }
                                                                                            if (!end && detail.WeekStartDate && detail.EndTime) {
                                                                                                end = moment.utc(`${moment(detail.WeekStartDate).format("YYYY-MM-DD")}T${extractUtcTime(detail.EndTime)}`).local().toDate();
                                                                                                isPlanned = true;
                                                                                            }

                                                                                            const fmt = (d) => d ? d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }) : "?";
                                                                                            let durationText = "0h 0m";
                                                                                            if (start && end) {
                                                                                                let dec = (end - start) / 3600000;
                                                                                                if (!isPlanned && detail.BreakTime > 0) dec -= detail.BreakTime / 60;
                                                                                                durationText = formatDuration(Math.max(0, Math.round(dec * 100) / 100));
                                                                                            }

                                                                                            return (
                                                                                                <>
                                                                                                    <div className="d-flex align-items-start justify-content-between mb-1 w-100">
                                                                                                        <p className="mb-0 fw-semibold me-2">{detail.TaskName}</p>
                                                                                                        <span className={`badge flex-shrink-0 ${isPlanned ? "bg-primary" : "bg-success"}`}>
                                                                                                            {isPlanned ? t("planned") : t("completed")}
                                                                                                        </span>
                                                                                                    </div>
                                                                                                    <p className="mb-1 text-muted small">
                                                                                                        {`${fmt(start)} – ${fmt(end)} | ${durationText}${!isPlanned && detail.BreakTime > 0 ? ` | Break: ${detail.BreakTime}m` : ""}`}
                                                                                                    </p>
                                                                                                </>
                                                                                            );
                                                                                        })()}
                                                                                    </div>
                                                                            {idx < order.OrderDetails.length - 1 && <hr className="my-2" />}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                )}
                            </Offcanvas.Body>
                        </>
                    )}
                </div>
            </Offcanvas>

            <Modal
                show={showInvoiceModal}
                onHide={() => {
                    setShowInvoiceModal(false);
                    resetInvoiceForm();
                }}
                centered
            >
                <Modal.Header closeButton>
                    <div>
                        <Modal.Title>{t("generateInvoice")}</Modal.Title>
                        <div className="text-muted small">
                            {t("generateInvoiceSubtitle")}
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body>

                    {/* CLIENT (UNCHANGED) */}
                    <Form.Group className="mb-3">
                        <Form.Label>
                            {t("selectClient")}
                        </Form.Label>

                        <div className="select-wrapper">
                            <Select
                                id="clientSelect"
                                options={[
                                    {
                                        label: t("clients"),
                                        options: dropdownOptions.map((item) => ({
                                            value: isEmployeeViewToggle ? item.UserId : item.RelationId,
                                            label: item.Name,
                                        })),
                                    },
                                ]}
                                value={
                                    dropdownOptions
                                        .map((item) => ({
                                            value: isEmployeeViewToggle ? item.UserId : item.RelationId,
                                            label: item.Name,
                                        }))
                                        .find((opt) => opt.value === selectedEntity) || null
                                }
                                onChange={(selectedOption) => {
                                    if (selectedOption?.value === "new") {
                                        navigate("/relations/new-relation");
                                    } else {
                                        setSelectedEntity(selectedOption?.value || "");
                                    }
                                }}
                                placeholder={t("searchLabel")}
                                noOptionsMessage={() => t("noOptionsAvailable")}
                                isClearable
                                className="custom-select-container"
                                classNamePrefix="custom-select"
                                components={{ GroupHeading: CustomGroupHeading }}
                                styles={{
                                    group: (provided) => ({
                                        ...provided,
                                        paddingTop: 0,
                                        paddingBottom: 0,
                                        margin: 0,
                                    }),
                                }}
                            />
                        </div>
                    </Form.Group>

                    {/* DATE FIELDS UPDATED */}
                    <Row>
                        <Col xs={12} md={6}>
                            <Form.Group className="position-relative">
                                <Form.Label>{t("dateFrom")}</Form.Label>

                                <DatePicker
                                    selected={invoiceFrom}
                                    onChange={(date) => setInvoiceFrom(date)}
                                    className="form-control w-100"
                                    placeholderText="dd-mm-yyyy"
                                    dateFormat="dd-MM-yyyy"
                                />
                            </Form.Group>
                        </Col>

                        <Col xs={12} md={6}>
                            <Form.Group className="position-relative">
                                <Form.Label>{t("dateTo")}</Form.Label>

                                <DatePicker
                                    selected={invoiceTo}
                                    onChange={(date) => setInvoiceTo(date)}
                                    className="form-control w-100"
                                    placeholderText="dd-mm-yyyy"
                                    dateFormat="dd-MM-yyyy"
                                    minDate={invoiceFrom}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                </Modal.Body>

                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setShowInvoiceModal(false);
                            resetInvoiceForm();
                        }}
                        className="btn-sm"
                    >
                        {t("cancel")}
                    </Button>

                    <Button
                        variant="info"
                        className="btn-sm d-flex align-items-center gap-2"
                        onClick={handlePreviewInvoice}
                        disabled={!isInvoiceFormValid || isPreviewLoading}
                    >
                        {isPreviewLoading && <Spinner size="sm" />}
                        {t("preview")}
                    </Button>

                    <Button
                        variant="primary"
                        className="btn-sm d-flex align-items-center gap-2"
                        onClick={handleGenerateInvoiceSubmit}
                        disabled={!isInvoiceFormValid || isDownloadLoading}
                    >
                        {isDownloadLoading && <Spinner size="sm" />}
                        {t("generateInvoice")}
                    </Button>
                </Modal.Footer>
            </Modal>

        </>
    );
};

export default HoursRegistration;